import type { MenuItemProps as AntMenuItemProps } from 'antd';
import { Spin } from 'antd';
import { LoadingOutlined } from '@ant-design/icons';
import React from 'react';
import { IconSize, IconType, IconWrap } from '../../../..';
import { Typography } from '../../../Typography';
import { MenuItemArrow, MenuItemContent, StyledMenuItem } from './styled';
import type { MenuItemState, MenuItemType } from './types';

export type { MenuItemType, MenuItemState };

export interface MenuItemProps
  extends Omit<AntMenuItemProps, 'danger' | 'disabled' | 'icon'> {
  type?: MenuItemType;
  state?: MenuItemState;
  children: React.ReactNode;
  className?: string;
  disabled?: boolean;
  loading?: boolean;
}

export const MenuItem: React.FC<MenuItemProps> = ({
  type = 'default',
  state = 'default',
  children,
  className = '',
  disabled,
  loading = false,
  ...antProps
}) => {
  const isDisabled = state === 'disabled' || disabled;
  const isInteractionDisabled = isDisabled || loading;
  const typographyVariant = type === 'bold' ? 'body1Bold' : 'body1';

  const contentRef = React.useRef<HTMLDivElement>(null);
  const [showArrow, setShowArrow] = React.useState(false);

  React.useEffect(() => {
    if (contentRef.current) {
      const isSubMenuTitle =
        contentRef.current.closest('.ant-menu-submenu-title') !== null;
      setShowArrow(isSubMenuTitle);
    }
  }, []);

  return (
    <StyledMenuItem
      $type={type}
      $state={state}
      disabled={isInteractionDisabled}
      className={className}
      {...antProps}
    >
      <MenuItemContent ref={contentRef} $showArrow={showArrow}>
        <Typography variant={typographyVariant} className="menu-item-content">
          {children}
        </Typography>
        {loading && (
          <Spin
            indicator={
              <LoadingOutlined
                style={{ fontSize: 12 }}
                spin
                onPointerEnterCapture={undefined}
                onPointerLeaveCapture={undefined}
              />
            }
            size="small"
          />
        )}
        {!!showArrow && (
          <MenuItemArrow
            $type={type}
            $state={isInteractionDisabled ? 'disabled' : state}
          >
            <IconWrap
              type={IconType.ChevronRight}
              size={IconSize.Small}
              className="menu-item-arrow"
            />
          </MenuItemArrow>
        )}
      </MenuItemContent>
    </StyledMenuItem>
  );
};
